// Tracks translation keys used during SSR/CSR and compares them against loaded keys.
// SSR uses AsyncLocalStorage so concurrent Express requests cannot share tracker state.

interface UsedKeyTracker {
  usedKeys: Set<string>;
  routeTags: string[];
  language: string;
  pageName: string;
  source: "ssr" | "csr";
}

interface TrackingContext {
  tracker: UsedKeyTracker | null;
  returnedKeys: Record<string, string> | null;
}

type AlsLike<T> = {
  run: <R>(store: T, callback: (...args: any[]) => R, ...args: any[]) => R;
  getStore: () => T | undefined;
};

// AsyncLocalStorage is Node-only — never import it into the client bundle.
let als: AlsLike<TrackingContext> | null = null;
let alsInitLogged = false;
if (typeof __SERVER__ !== "undefined" && __SERVER__) {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
    const { AsyncLocalStorage } = require("async_hooks") as {
      AsyncLocalStorage: new <T>() => AlsLike<T>;
    };
    als = new AsyncLocalStorage<TrackingContext>();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[i18n] AsyncLocalStorage unavailable — SSR key tracking disabled", err);
  }
  if (!alsInitLogged) {
    alsInitLogged = true;
    // eslint-disable-next-line no-console
    console.info(`[i18n] translation tracking ALS_ACTIVE=${Boolean(als)}`);
  }
}

// Client SPA only — never used as an SSR fallback (that caused cross-request leaks).
const clientFallbackContext: TrackingContext = {
  tracker: null,
  returnedKeys: null,
};

const isServer = (): boolean => typeof __SERVER__ !== "undefined" && Boolean(__SERVER__);

const getContext = (): TrackingContext | null => {
  if (isServer()) {
    // Fail closed on SSR: no shared mutable tracker if ALS is missing/outside run().
    if (!als) return null;
    return als.getStore() ?? null;
  }
  return clientFallbackContext;
};

/**
 * Run SSR work inside a request-scoped tracking context.
 * Prefer als.run() over enterWith — enterWith can leak across concurrent Express requests.
 */
export const withTranslationTracking = <T>(fn: () => T): T => {
  const store: TrackingContext = { tracker: null, returnedKeys: null };
  if (als) {
    return als.run(store, fn);
  }
  if (isServer()) {
    // No ALS on server → run without tracking (never share a process-global tracker).
    return fn();
  }
  clientFallbackContext.tracker = null;
  clientFallbackContext.returnedKeys = null;
  return fn();
};

export const setReturnedTranslationKeys = (keys: Record<string, string>): void => {
  const ctx = getContext();
  if (ctx) {
    ctx.returnedKeys = keys;
  }
};

// init tracking new ssr/csr request
export const initializeKeyTracking = (
  routeTags: string[],
  language: string,
  pageName: string,
  source: "ssr" | "csr" = isServer() ? "ssr" : "csr",
): void => {
  const ctx = getContext();
  if (!ctx) return;

  ctx.tracker = {
    usedKeys: new Set<string>(),
    routeTags,
    language,
    pageName,
    source,
  };
};

export const trackKeyUsage = (key: string, isDynamic = false): void => {
  // Skip tracking for dynamic keys - they won't trigger Sentry alerts
  if (isDynamic) return;

  const ctx = getContext();
  if (ctx?.tracker && key && typeof key === "string") {
    ctx.tracker.usedKeys.add(key);
  }
};

// CMS/HTML/URL strings go through t(). Words stay trackable; markup and URLs do not.
// Bare hostnames only (onlinevelorent.com, www.foo.co.uk) — not keys like home.topcity.uk.
const LOOKS_LIKE_HOSTNAME =
  /^(www\.)?[a-z0-9-]+(\.(co|ac|org|com))?\.(com|net|org|io|co|uk|info|edu|gov|cn)$/i;
// Real dot keys use camelCase (costOfLiving), snake_case (main_banner_desktop) and a few
// `mischlaneous.*` keys with ( ) & — rejecting those would silence genuine missing-key alerts.
const VALID_DOT_KEY = /^[\w.()&-]+$/;

export const isValidTranslationKey = (key: string, requireDot = false): boolean => {
  if (!key || key.length < 3) return false;

  if (
    key.includes(" ") ||
    key.includes("<") ||
    key.includes(">") ||
    key.includes("/") ||
    key.includes("://") ||
    /[\r\n\t]/.test(key) ||
    key.includes("%") ||
    /^[A-Z]/.test(key) ||
    /^[0-9]/.test(key) ||
    key.includes("@") ||
    key.includes("+") ||
    /^https?:\/\//.test(key) ||
    LOOKS_LIKE_HOSTNAME.test(key) ||
    key.length > 100
  ) {
    return false;
  }

  if (requireDot) {
    return key.includes(".") && VALID_DOT_KEY.test(key);
  }

  if (!key.includes(".")) {
    return /^[a-z]+$/.test(key);
  }

  return VALID_DOT_KEY.test(key);
};

const isDotFormattedKey = (key: string): boolean => key.includes(".");

type TranslationAlertOptions = {
  level: "warning" | "error";
  tags: Record<string, string>;
  extra: Record<string, unknown>;
  fingerprint?: string[];
};

/** Send translation alerts from SSR (@sentry/node) or CSR (window.Sentry). */
const captureTranslationAlert = (message: string, options: TranslationAlertOptions): void => {
  if (typeof window !== "undefined" && typeof window.Sentry !== "undefined") {
    window.Sentry.captureMessage(message, options);
    return;
  }

  if (typeof global !== "undefined" && __SERVER__) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
      const Sentry = require("@sentry/node");
      Sentry.captureMessage(message, options);
    } catch {
      // Sentry not initialized — server logs remain the fallback
    }
  }
};

export const reportEmptyTranslationLoad = ({
  language,
  routeTags,
  pageName,
  source,
}: {
  language: string;
  routeTags: string[];
  pageName: string;
  source: "ssr" | "csr";
}): void => {
  captureTranslationAlert("Critical: Translation load returned zero keys", {
    level: "error",
    fingerprint: ["empty-translation-load", String(pageName), language, source],
    tags: {
      page: String(pageName),
      language,
      source,
      routeTags: routeTags.join(","),
      severity: "critical",
    },
    extra: {
      type: "EMPTY_TRANSLATION_LOAD",
      language,
      routeTags,
      page: pageName,
      source,
      timestamp: new Date().toISOString(),
    },
  });
};

export const getCurrentTracker = (): UsedKeyTracker | null => getContext()?.tracker ?? null;

export const compareAndAlert = (
  returnedKeys: Record<string, string>,
  isLoadingTranslations = false,
): void => {
  const tracker = getContext()?.tracker;
  if (!tracker) {
    return;
  }

  // Skip Sentry alerts if translations are still loading to avoid false positives
  if (isLoadingTranslations) {
    return;
  }

  const { usedKeys, routeTags, language, pageName, source } = tracker;
  const returnedKeySet = new Set(Object.keys(returnedKeys));

  // Separate valid translation keys from dynamic/hardcoded strings
  const allUsedKeys = Array.from(usedKeys);
  const validKeys = allUsedKeys.filter((key) => isValidTranslationKey(key));

  // Find missing keys (valid keys that aren't returned by the loader)
  const missingKeys = validKeys.filter((key) => !returnedKeySet.has(key));
  const missingDotFormattedKeys = missingKeys.filter(isDotFormattedKey);

  if (typeof window !== "undefined" && (window as any).__reduxDispatch) {
    (window as any).__reduxDispatch({
      type: "appData/setMissingTranslationKeys",
      payload: missingKeys,
    });
  }

  if (missingKeys.length > 0) {
    const alertPayload = {
      type: "MISSING_TRANSLATION_KEYS",
      page: pageName,
      language,
      routeTags,
      source,
      missingKeys,
      validKeyCount: validKeys.length,
      returnedKeyCount: returnedKeySet.size,
      timestamp: new Date().toISOString(),
    };

    captureTranslationAlert("Missing Translation Keys Detected", {
      level: "warning",
      fingerprint: ["missing-translation-keys", String(pageName), language, source],
      tags: {
        page: String(pageName),
        language,
        source,
        missingKeyCount: String(missingKeys.length),
      },
      extra: alertPayload,
    });
  }

  // critical alert for missing keys — stable fingerprint (no key-list join)
  if (missingDotFormattedKeys.length > 0) {
    const sampleKeys = missingDotFormattedKeys.slice(0, 20);
    const dotFormattedAlertPayload = {
      type: "MISSING_DOTFORMATTED_TRANSLATION_KEYS",
      page: pageName,
      language,
      routeTags,
      source,
      missingDotFormattedKeys,
      missingKeyCount: missingDotFormattedKeys.length,
      timestamp: new Date().toISOString(),
    };

    captureTranslationAlert("Critical: Missing Dot-Formatted Translation Keys", {
      level: "error",
      fingerprint: ["missing-dotformatted-keys", String(pageName), language, source],
      tags: {
        page: String(pageName),
        language,
        source,
        missingKeyCount: String(missingDotFormattedKeys.length),
        routeTags: routeTags.join(","),
        sampleKeys: sampleKeys.join(","),
        severity: "critical",
      },
      extra: dotFormattedAlertPayload,
    });
  }
};

export const performPostRenderComparison = (): void => {
  const ctx = getContext();
  if (!ctx?.returnedKeys) {
    resetTracking();
    return;
  }

  compareAndAlert(ctx.returnedKeys);
  resetTracking();
};

export const resetTracking = (): void => {
  const ctx = getContext();
  if (!ctx) return;
  ctx.tracker = null;
  ctx.returnedKeys = null;
};

// Bumps on each client init so stale 3s timers from prior SPA navigations no-op.
let clientTrackingGeneration = 0;
let clientComparisonTimer: ReturnType<typeof setTimeout> | null = null;

//  Client-side tracking for SPA navigation
export const initializeClientSideTracking = (
  routeTags: string[],
  language: string,
  pageName: string,
): void => {
  clientTrackingGeneration += 1;
  if (clientComparisonTimer) {
    clearTimeout(clientComparisonTimer);
    clientComparisonTimer = null;
  }
  initializeKeyTracking(routeTags, language, pageName, "csr");
};

export const performClientSideComparison = (
  returnedKeysOrGetter: Record<string, string> | (() => Record<string, string>),
  getIsLoading?: () => boolean,
): void => {
  const generation = clientTrackingGeneration;
  if (clientComparisonTimer) {
    clearTimeout(clientComparisonTimer);
    clientComparisonTimer = null;
  }

  clientComparisonTimer = setTimeout(() => {
    clientComparisonTimer = null;
    // Stale timer from a previous navigation — do not alert with wrong routeTags.
    if (generation !== clientTrackingGeneration) return;

    if (getContext()?.tracker) {
      const isLoading = getIsLoading ? getIsLoading() : false;
      const returnedKeys =
        typeof returnedKeysOrGetter === "function" ? returnedKeysOrGetter() : returnedKeysOrGetter;
      compareAndAlert(returnedKeys, isLoading);
      resetTracking();
    }
  }, 3000);
};
